import { Database } from '@/types/database.types';
import { functions } from '@/types/functions.types';
import { createClient } from '@supabase/supabase-js';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

export const supabase = createClient<Database>(
  import.meta.env['VITE_SUPABASE_DATABASE_URL'],
  import.meta.env['VITE_SUPABASE_ANON_KEY'],
  {
    auth: {
      flowType: 'pkce',
      autoRefreshToken: true,
      detectSessionInUrl: true,
      persistSession: true
    }
  }
);

export const invokeFunction = async <T extends keyof typeof functions>(functionName: T, input: z.infer<typeof functions[T]["input"]>): Promise<z.infer<typeof functions[T]["output"]>> => {
  const { data, error } = await supabase.functions.invoke(functionName, {
    body: input ?? {},
    headers: { "x-swim-homes-environment": import.meta.env.MODE }
  });

  if (error) { throw error; }

  return functions[functionName].output.parse(data);
};

export const useSupabaseFunction = <T extends keyof typeof functions>(functionName: T) => {
  if (!functions[functionName]) {
    throw new Error(`Function ${functionName} is not defined in functions types`);
  }

  type InputType = z.infer<typeof functions[T]["input"]>;
  type OutputType = z.infer<typeof functions[T]["output"]>;

  return useMutation<OutputType, Error, InputType>({
    mutationFn: async (input: InputType) => {
      return await invokeFunction(functionName, input);
    },
  })
};